import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { IWebsiteBuilderEditorHistoryActionBase, IWebsiteBuilderHistorySnapshot, WebsiteBuilderEditorActionType } from "../../websiteBuilderEditorAction";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { cloneDeep } from "lodash";
import { TWebsiteBuilderContainer } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderContainer";
import { TWebsiteBuilderSectionEntityLayout } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";
import { IWebsiteBuilderGeneralTraversalNode } from "../../actionHelpers/generalTraversal";
import { containerTraversal } from "../../actionHelpers/containerTraversal";

export interface IMoveContainerActionPayload {
  id: string;
  prevIndex: number;
  nextIndex: number;
  prevZIndex: number;
  nextZIndex: number;
  prevSectionId: string;
  nextSectionId: string;
  prevLayout: TWebsiteBuilderSectionEntityLayout;
  nextLayout: TWebsiteBuilderSectionEntityLayout;
}

export interface IMoveContainerAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.MOVE_CONTAINER;
  payload: IMoveContainerActionPayload;
}

export function getMoveContainerInverseAction(action: IMoveContainerAction): IMoveContainerAction {
  const {
    id,
    prevIndex,
    nextIndex,
    prevZIndex,
    nextZIndex,
    prevSectionId,
    nextSectionId,
    nextLayout,
    prevLayout,
  } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.MOVE_CONTAINER,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: action.scrollNodeId,
    payload: {
      id,
      prevIndex: nextIndex,
      nextIndex: prevIndex,
      prevZIndex: nextZIndex,
      nextZIndex: prevZIndex,
      prevSectionId: nextSectionId,
      nextSectionId: prevSectionId,
      prevLayout: cloneDeep(nextLayout),
      nextLayout: cloneDeep(prevLayout),
    },
  };
}

function moveContainerToOtherSectionTraversal(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  id: string,
  nextLayout: TWebsiteBuilderSectionEntityLayout,
  nextIndex: number,
  nextZIndex: number,
  prevSectionId: string,
  nextSectionId: string,
): IWebsiteBuilderHistorySnapshot {
  let updatedContainer: TWebsiteBuilderContainer = null;

  function deleteTraverse(node: IWebsiteBuilderGeneralTraversalNode): IWebsiteBuilderGeneralTraversalNode {
    if (!node.children) {
      return node;
    } else {
      if (node.id === prevSectionId) {
        const oldContainer = node.children.find(child => child.id === id) as TWebsiteBuilderContainer;

        if (oldContainer != null && updatedContainer == null) {
          updatedContainer = cloneDeep(oldContainer);

          updatedContainer.container.layout = nextLayout;
          updatedContainer.container.zIndex = nextZIndex;
        }

        return {
          ...node,
          children: node.children
            .filter(child => child.id !== id)
            .map(deleteTraverse),
        };
      } else {
        return {
          ...node,
          children: node.children.map(deleteTraverse),
        };
      }
    }
  }

  const headerAfterDelete = deleteTraverse(header) as IWebsiteBuilderHeader;
  const rootAfterDelete = deleteTraverse(root) as IWebsiteBuilderRoot;
  const footerAfterDelete = deleteTraverse(footer) as IWebsiteBuilderFooter;

  if (updatedContainer == null) {
    return {
      header,
      root,
      footer,
    };
  }

  function addTraverse(node: IWebsiteBuilderGeneralTraversalNode): IWebsiteBuilderGeneralTraversalNode {
    if (!node.children) {
      return node;
    } else {
      if (node.id === nextSectionId) {
        const updatedChildren = [
          ...node.children.slice(0, nextIndex),
          updatedContainer,
          ...node.children.slice(nextIndex),
        ];

        return {
          ...node,
          children: updatedChildren.map(addTraverse),
        };
      } else {
        return {
          ...node,
          children: node.children.map(addTraverse),
        };
      }
    }
  }

  const headerAfterAdding = addTraverse(headerAfterDelete) as IWebsiteBuilderHeader;
  const rootAfterAdding = addTraverse(rootAfterDelete) as IWebsiteBuilderRoot;
  const footerAfterAdding = addTraverse(footerAfterDelete) as IWebsiteBuilderFooter;

  return {
    header: headerAfterAdding,
    root: rootAfterAdding,
    footer: footerAfterAdding,
  };
}

export function applyMoveContainerChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: IMoveContainerAction,
): IWebsiteBuilderHistorySnapshot {
  const { id, prevSectionId, nextSectionId, nextLayout, nextZIndex, nextIndex } = action.payload;

  if (prevSectionId === nextSectionId) {
    return containerTraversal(
      header,
      root,
      footer,
      id,
      { layout: cloneDeep(nextLayout) },
    );
  } else {
    return moveContainerToOtherSectionTraversal(
      header,
      root,
      footer,
      id,
      cloneDeep(nextLayout),
      nextIndex,
      nextZIndex,
      prevSectionId,
      nextSectionId,
    );
  }
}
